import { THREE } from "@scrolly/renderer";

export const AssetLibraryType = {
    texture: 0,
    textureCube: 1,
} as const;
export type AssetLibraryType = (typeof AssetLibraryType)[keyof typeof AssetLibraryType];

export const AssetLibraryCategory = {
    roughness: 0,
    matcap: 1,
    skybox: 2,
    texture: 3,
} as const;
export type AssetLibraryCategory = (typeof AssetLibraryCategory)[keyof typeof AssetLibraryCategory];

interface AssetQueued {
    id: string;
    type: AssetLibraryType;
    // Legacy code typing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content: any;
    category: AssetLibraryCategory;
}

class AssetLibrary {
    assets: Array<{
        texture: THREE.Texture;
        id: string;
        loaded: boolean;
        category: AssetLibraryCategory;
    }> = [];
    queue: Array<AssetQueued> = [];
    allLoaded = false;

    getAsset(id: string) {
        const asset = this.assets.find((m) => m.id === id);
        return asset?.texture;
    }

    queueAsset(asset: AssetQueued) {
        const { id, category, type, content } = asset;
        this.queue.push({
            id,
            type,
            category,
            content,
        });
    }

    loadAsset(asset: AssetQueued, callback?: TODOAny) {
        const { id, category, type, content } = asset;
        switch (type) {
            case AssetLibraryType.texture:
                return this.loadTexture2D(content, (texture) => {
                    if (callback) {
                        callback(texture);
                    }
                    this.pushTexture(category, id, texture);
                });
            case AssetLibraryType.textureCube:
                return this.loadTextureCube(content, (texture) => {
                    if (callback) {
                        callback(texture);
                    }
                    this.pushTexture(category, id, texture);
                });
            default:
                return undefined;
        }
    }

    loadAssets() {
        const promises = this.queue.map(
            (asset) =>
                new Promise((resolve) => {
                    this.loadAsset(asset, resolve);
                })
        );
        this.queue = [];
        return Promise.all(promises).then(() => {
            this.allLoaded = true;
        });
    }

    pushTexture(category: AssetLibraryCategory, id: string, texture: THREE.Texture) {
        this.assets.push({
            id,
            texture,
            category,
            loaded: true,
        });
    }

    loadTexture2D(image: TODOAny, callback: (texture: THREE.Texture) => void): THREE.Texture {
        const loader = new THREE.TextureLoader();
        return loader.load(image, (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.minFilter = THREE.LinearMipMapLinearFilter;
            texture.magFilter = THREE.LinearFilter;
            callback(texture);
        });
    }

    loadTextureCube(images: TODOAny, callback: (texture: THREE.Texture) => void): THREE.CubeTexture {
        const loader = new THREE.CubeTextureLoader();
        return loader.load(images, callback);
    }
}

export default AssetLibrary;
